import { z } from "zod";
import { OrderSchema, OrderStatusSchema } from "./entities/order.js";
import { OrderingStateSchema } from "./dto/store.js";

export const MenuItemUpsertedEventSchema = z.object({
  type: z.literal("menu:item.upserted"),
  id: z.string().uuid(),
  at: z.string().datetime(),
});

export const MenuItemDeletedEventSchema = z.object({
  type: z.literal("menu:item.deleted"),
  id: z.string().uuid(),
  at: z.string().datetime(),
});

export const OrderCreatedEventSchema = z.object({
  type: z.literal("order:created"),
  order: OrderSchema,
  at: z.string().datetime(),
});

export const OrderStatusUpdatedEventSchema = z.object({
  type: z.literal("order:status.updated"),
  id: z.string().uuid(),
  status: OrderStatusSchema,
  at: z.string().datetime(),
});

export const OrderItemsAddedEventSchema = z.object({
  type: z.literal("order:items.added"),
  id: z.string().uuid(),
  additionId: z.string().uuid(),
  at: z.string().datetime(),
});

export const StoreOrderingUpdatedEventSchema = z.object({
  type: z.literal("store:ordering.updated"),
  ordering: OrderingStateSchema,
  at: z.string().datetime(),
});

export const KitchenScreensUpdatedEventSchema = z.object({
  type: z.literal("kitchen:screens.updated"),
  open: z.number().int().nonnegative(),
  at: z.string().datetime(),
});

export const RealtimeEventSchema = z.discriminatedUnion("type", [
  MenuItemUpsertedEventSchema,
  MenuItemDeletedEventSchema,
  OrderCreatedEventSchema,
  OrderStatusUpdatedEventSchema,
  OrderItemsAddedEventSchema,
  StoreOrderingUpdatedEventSchema,
  KitchenScreensUpdatedEventSchema,
]);
export type RealtimeEvent = z.infer<typeof RealtimeEventSchema>;

export const SOCKET_EVENT_NAMES = {
  menuUpserted: "menu:item.upserted",
  menuDeleted: "menu:item.deleted",
  orderCreated: "order:created",
  orderStatusUpdated: "order:status.updated",
  orderItemsAdded: "order:items.added",
  storeOrderingUpdated: "store:ordering.updated",
  kitchenScreensUpdated: "kitchen:screens.updated",
} as const;

export const KITCHEN_SCREEN_EVENTS = {
  open: "kitchen:screen.open",
  close: "kitchen:screen.close",
} as const;

export const tenantRoom = (tenantId: string, topic: "menu" | "orders" | "kitchen") =>
  `tenant:${tenantId}:${topic}`;

/**
 * Per-user room every socket joins alongside its tenant rooms, so a
 * revocation can reach every socket a given user holds — regardless of
 * which store's rooms it is also in — via one room name rather than a
 * per-connection lookup. See `MEMBER_REVOKED_EVENT`.
 */
export const userRoom = (userId: string) => `user:${userId}`;

/**
 * Internal, server-to-server signal published with Socket.IO's
 * `serverSideEmit` when a member is removed from an organization — never
 * emitted to a client socket directly (see `sockets/index.ts`). Every API
 * instance re-verifies membership for its own locally-held sockets in
 * `userRoom(userId)` before disconnecting any of them, rather than trusting
 * this payload, so this only needs to say *who* to re-check, not *why*.
 */
export const MEMBER_REVOKED_EVENT = "member:revoked";

export interface MemberRevokedPayload {
  userId: string;
  organizationId: string;
}

export const STORE_BLOCKED_EVENT = "store:blocked";

export interface StoreBlockedPayload {
  organizationId: string;
}
